import { useCallback, useEffect, useState } from 'react';
import { auth } from '../auth/firebase';

export type RoleAccess = 'checking' | 'allowed' | 'denied';

/**
 * Whether the signed-in account's role (the `role` claim on its sign-in token) is one of `allowedRoles`.
 * It stays 'checking' until that is known, so a page can show none of itself before then: a saved copy of its data would flash
 * before the denial. `deny()` lets a page mark access as refused when Firestore itself refuses it (permission-denied).
 * The Firestore rules and the functions are what really protect the data; this is what the person sees.
 */
export function useRoleAccess(allowedRoles: readonly string[]): { access: RoleAccess; deny: () => void } {
  const [access, setAccess] = useState<RoleAccess>('checking');
  const roleList = allowedRoles.join(',');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // After a page reload Firebase has not restored the signed-in user yet: wait for it, or the check would find nobody
      await auth.authStateReady();
      const user = auth.currentUser;
      let result: RoleAccess = 'allowed';
      if (user) {
        try {
          let role = (await user.getIdTokenResult()).claims.role;
          // No role on the token at all: it may only just have been added, so ask for a fresh token once
          if (!role) role = (await user.getIdTokenResult(true)).claims.role;
          if (!roleList.split(',').includes(String(role))) result = 'denied';
        } catch (error) {
          // Could not read the token (for example offline): do not lock anyone out here; the rules still apply
          console.error('[useRoleAccess] Could not read the role from the sign-in token:', error);
        }
      }
      if (!cancelled) setAccess((current) => (current === 'denied' ? current : result));
    })();
    return () => { cancelled = true; };
  }, [roleList]);

  const deny = useCallback(() => setAccess('denied'), []);
  return { access, deny };
}
